import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { updateBatchDetails } from 'src/lib/redux';

import { detailsFetcher } from '../../fetchers/batches-fetcher';
import { FeeId } from '../../models/batch-models';
import { VideoDetails } from './useTodaySchedule';

type Props = {
  data: Record<string, any> | null;
  isLoading: boolean;
};
export const BATCH_DETAILS_QUERY_KEY = (batchSlug: string) => 'batch-details-' + batchSlug;
const useBatchDetails = ({
  batchSlug,
  enabled,
}: {
  enabled?: boolean;
  batchSlug: string;
}): Props => {
  const dispatch = useDispatch();

  const { data, isLoading } = useQuery(
    [
      BATCH_DETAILS_QUERY_KEY(batchSlug),
      {
        batchSlug,
      },
    ],
    detailsFetcher,
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: enabled !== false && !!batchSlug,
      staleTime: 1000 * 60 * 60,
      onSuccess: (responseData) => {
        dispatch(updateBatchDetails({ batchDetails: { ...responseData?.data } }));
      },
    },
  );

  return {
    data: data ?? {},
    isLoading,
  };
};

export class BatchDetailModel {
  _id: string;
  type: string;
  name: string;
  byName: string;
  description: string;
  batchCode: string;
  startDate: string;
  endDate: string;
  programId: string;
  fee: Fee;
  isPurchased: boolean;
  isBlocked: boolean;
  isChatBlocked: boolean;
  isSelfLearning: boolean;
  subjects: Subject[];
  isAvailableFromPoints: boolean;
  maxWalletPoint: number;
  registrationStartDate: string;
  registrationEndDate: string;
  previewImage: FileModal;
  batchPdf: BatchPdf;
  previewVideoType: string;
  previewVideoUrl?: string;
  previewVideoId?: any;
  faqCat: string;
  attachTestCat: boolean;
  testCat: string;
  testCats: string[];
  meta: Meta[];
  previewVideoDetails: VideoDetails;
  enableStudyMaterial: boolean;
  enableCommunity: boolean;
  studyMaterials: StudyMaterial[];
  markedAsNew: boolean;
  isClassLive: boolean;
  isPathshala: boolean;
  program: Program;
  slug: string;
  exam: string[];
  khazanaProgramId: string;
  class: string;
  board: string;
  language: string;
  isActive: boolean;
  isNeet: string;
  teachers: Teacher[];
  priceLabel: string;
  startingPlan: FeeId;
  planCount: number;
  config: BatchDetailConfig;
  isDoubtEnabled: boolean;
  isDoubtRecommendationEnabled;
  template?: string;
  masterBatchId?: string;

  constructor(data: any) {
    data = data || {};
    this._id = data._id || '';
    this.type = data.type || '';
    this.name = data.name || '';
    this.priceLabel = data.priceLabel || '';
    this.description = data.description || '';
    this.batchCode = data.batchCode || '';
    this.programId = data.programId || '';
    this.previewVideoType = data.previewVideoType || '';
    this.faqCat = data.faqCat || '';
    this.testCat = data.testCat || '';
    this.maxWalletPoint = data.maxWalletPoint || 0;
    this.planCount = data.planCount || 0;
    this.isPurchased = data.isPurchased || false;
    this.isBlocked = data.isBlocked || false;
    this.isAvailableFromPoints = data.isAvailableFromPoints || false;
    this.isSelfLearning = data.isSelfLearning || false;
    this.attachTestCat = data.attachTestCat || false;
    this.enableStudyMaterial = data.enableStudyMaterial || false;
    this.enableCommunity = data.enableCommunity || false;
    this.markedAsNew = data.markedAsNew || false;
    this.isClassLive = data.isClassLive || false;
    this.isPathshala = data.isPathshala || false;
    this.startDate = data.startDate || '';
    this.endDate = data.endDate || '';
    this.registrationStartDate = data.registrationStartDate || '';
    this.registrationEndDate = data.registrationEndDate || '';
    this.startingPlan = new FeeId(data.startingPlan) || {};
    this.fee = new Fee(data.fee) || new Fee({});
    this.previewImage = new FileModal(data.previewImage);
    this.batchPdf = new BatchPdf(data.batchPdf) || {};
    this.program = new Program(data.program) || {};
    this.testCats = data.testCats || [];
    this.meta = data.meta || [];
    this.slug = data.slug || '';
    this.exam = data.exam || [];
    this.previewVideoDetails = new VideoDetails(data.previewVideoDetails) || new VideoDetails({});
    this.isChatBlocked = data.isChatBlocked || false;
    this.khazanaProgramId = data.khazanaProgramId || '';
    this.class = data.class || '';
    this.board = data.board || '';
    this.language = data.language || '';
    this.isNeet = data.isNeet || '';
    this.isActive = data.isActive || false;
    this.teachers = data.teachers || [];
    this.previewVideoUrl = data.previewVideoUrl || '';
    this.byName = data.byName || '';
    this.isDoubtEnabled = data.isDoubtEnabled || false;
    this.isDoubtRecommendationEnabled = data.isDoubtRecommendationEnabled || false;
    this.config = new BatchDetailConfig(data.config) || new BatchDetailConfig({});

    if (data.subjects) {
      this.subjects = data.subjects.map((subject: any) => new Subject(subject));
    } else {
      this.subjects = [];
    }

    if (data.studyMaterials) {
      this.studyMaterials = data.studyMaterials.map((res: any) => new StudyMaterial(res));
    } else {
      this.studyMaterials = [];
    }
    this.template = data.template || '';
    this.masterBatchId = data.masterBatchId || '';
  }
}

export class BatchDetailConfig {
  enableBatchReferAndEarn: boolean;
  isVidyapeeth: boolean;
  isFormEnabled: boolean;
  isSubjective: boolean;
  constructor(data: any) {
    data = data || {};
    this.enableBatchReferAndEarn = data.enableBatchReferAnd || false;
    this.isVidyapeeth = data.isVidyapeeth || false;
    this.isFormEnabled = data.isFormEnabled || false;
    this.isSubjective = data.isSubjective || false;
  }
}

export class Teacher {
  name: string;
  _id: string;

  constructor(data: any) {
    data = data || {};
    this.name = data.name || '';
    this._id = data._id || '';
  }
}

export class StudyMaterial {
  _id: string;
  type: string;
  typeId: string;

  constructor(data: any) {
    data = data || {};
    this._id = data._id || '';
    this.type = data._id || '';
    this.typeId = data._id || '';
  }
}

export class FileId {
  _id: string;
  baseUrl: string;
  key: string;
  name: string;

  constructor(data: any) {
    data = data || {};
    this._id = data._id || '';
    this.baseUrl = data.baseUrl || '';
    this.key = data.key || '';
    this.name = data.name || '';
  }
}

export class BatchPdf {
  _id: string;
  baseUrl: string;
  name: string;
  key: string;

  constructor(data: any) {
    data = data || {};
    this._id = data._id || '';
    this.baseUrl = data.baseUrl || '';
    this.name = data.name || '';
    this.key = data.key || '';
  }
}

export class Subject {
  _id: string;
  fileId: FileId;
  imageId: ImageId;
  subject: string;
  schedules: Schedule[];
  teacherIds: TeacherId[];
  slug: string;
  subjectId: string;
  qbgSubjectId: string;

  constructor(data: any) {
    data = data || {};
    this._id = data._id || '';
    this.fileId = data.fileId || '';
    this.imageId = data.imageId || '';
    this.subjectId = data.subjectId || '';
    this.subject = data.subject || '';
    this.schedules = data.schedules || '';
    this.teacherIds = data.teacherIds || '';

    if (data?.teacherIds) {
      this.teacherIds = data.teacherIds.map((res: any) => new TeacherId(res));
    } else {
      this.teacherIds = [];
    }

    if (data?.schedules) {
      this.schedules = data.schedules.map((res: any) => new Schedule(res));
    } else {
      this.schedules = [];
    }
    this.slug = data.slug || '';
    this.qbgSubjectId = data.qbgSubjectId || '';
  }
}

export class TeacherId {
  _id: string;
  firstName: string;
  lastName: string;
  imageId: ImageId;
  qualification: string;
  url: string;
  experience: string;

  constructor(data: any) {
    data = data || {};
    this._id = data._id || '';
    this.firstName = data.firstName || '';
    this.lastName = data.lastName || '';
    this.imageId = new ImageId(data.imageId) || new ImageId({});
    this.qualification = data.qualification || '';
    this.url = data.url || '';
    this.experience = data.experience || '';
  }
}

export class Schedule {
  _id: string;
  day: string;
  startTime: string;
  endTime: string;

  constructor(data: any) {
    data = data || {};
    this._id = data._id || '';
    this.day = data.day || '';
    this.startTime = data.startTime || '';
    this.endTime = data.endTime || '';
  }
}

export class ImageId {
  _id: string;
  baseUrl: string;
  name: string;
  key: string;

  constructor(data: any) {
    data = data || {};
    this._id = data._id || '';
    this.baseUrl = data.baseUrl || '';
    this.name = data.name || '';
    this.key = data.key || '';
  }
}

export class Fee {
  amount: number;
  tax: number;
  discount: number;
  total: number;

  constructor(data: any) {
    data = data || {};
    this.amount = data.amount || 0;
    this.tax = data.tax || 0;
    this.discount = data.discount || 0;
    this.total = data.total || 0;
  }
}

export class FileModal {
  _id: string;
  baseUrl: string;
  key: string;
  name: string;
  url: string;
  status: string;
  iconUrl: string;

  constructor(file: any) {
    file = file || {};
    this._id = file._id || '';
    if (file.base_url) {
      this.baseUrl = file.base_url || '';
    } else if (file.baseUrl) {
      this.baseUrl = file.baseUrl;
    } else {
      this.baseUrl = '';
    }

    this.key = file.key || '';
    this.name = file.name || '';
    this.url = this.baseUrl + this.key || '';
    this.status = '';
    this.iconUrl = '';
  }
}

export class Program {
  _id: string;
  name: string;
  board: string;
  class: string;
  exam: string[];
  language: string;

  constructor(data: any) {
    data = data || {};
    this._id = data._id || '';
    this.name = data.name || '';
    this.board = data.board || '';
    this.class = data.class || '';
    this.exam = data.exam || '';
    this.language = data.language || '';
  }
}

export class Meta {
  _id: string;
  key: string;
  value: string;
  displayOrder: number;

  constructor(data: any) {
    data = data || {};
    this._id = data._id || '';
    this.key = data.key || '';
    this.value = data.value || '';
    this.displayOrder = data.displayOrder || '';
  }
}

export default useBatchDetails;
